/**
 * Manages state persistence using sessionStorage
 */
export class StateManager {
  constructor(element) {
    this.element = element
    this.storageKey = 'sleevingSelector'
  }

  /**
   * Save current state to sessionStorage
   */
  save(sliderManager) {
    const state = {
      // Option buttons
      prefColor: this.element.querySelector("button[name='pref-color'].active")?.value || '0',
      prefWrap: this.element.querySelector("button[name='pref-wrap'].active")?.value || '2',
      prefTool: this.element.querySelector("button[name='pref-tool'].active")?.value || '2',
      prefFlex: this.element.querySelector("button[name='pref-flex'].active")?.value || '0',
      
      // Sort order
      sortOrder: this.element.querySelector("select[name='sort-order']")?.value || 'catalog',
      
      // Slider toggles
      tempToggle: this.element.querySelector("input[name='tempToggle']")?.checked ?? true,
      expToggle: this.element.querySelector("input[name='expToggle']")?.checked ?? true,
      abraToggle: this.element.querySelector("input[name='abraToggle']")?.checked ?? true,
      
      // Slider values from SliderManager
      ...sliderManager.getState()
    }
    
    sessionStorage.setItem(this.storageKey, JSON.stringify(state))
  }

  /**
   * Restore state from sessionStorage
   */
  restore(sliderManager) {
    const saved = sessionStorage.getItem(this.storageKey)
    if (!saved) return false
    
    try {
      const state = JSON.parse(saved)
      
      // Restore option buttons
      this.restoreOptionButton('pref-color', state.prefColor)
      this.restoreOptionButton('pref-wrap', state.prefWrap)
      this.restoreOptionButton('pref-tool', state.prefTool)
      this.restoreOptionButton('pref-flex', state.prefFlex)
      
      // Restore sort order
      const sortSelect = this.element.querySelector("select[name='sort-order']")
      if (sortSelect && state.sortOrder) {
        sortSelect.value = state.sortOrder
      }
      
      // Restore slider toggles and values
      this.restoreSliderToggle('tempToggle', state.tempToggle)
      this.restoreSliderToggle('expToggle', state.expToggle)
      this.restoreSliderToggle('abraToggle', state.abraToggle)
      
      // Restore slider values via SliderManager
      sliderManager.restoreValues(
        state.sliderTempMin,
        state.sliderTempMax,
        state.sliderExpansionMin,
        state.sliderExpansionMax,
        state.sliderAbrasionMin,
        state.sliderAbrasionMax
      )

      return true
    } catch (e) {
      console.error('Failed to restore sleeving selector state:', e)
      this.clear()
      return false
    }
  }

  /**
   * Restore option button selection
   */
  restoreOptionButton(name, value) {
    if (!value) return
    
    const buttons = this.element.querySelectorAll(`button[name="${name}"]`)
    buttons.forEach(btn => {
      if (btn.value === value) {
        btn.classList.add('active')
        btn.setAttribute('aria-pressed', 'true')
      } else {
        btn.classList.remove('active')
        btn.setAttribute('aria-pressed', 'false')
      }
    })
  }

  /**
   * Restore slider toggle state
   */
  restoreSliderToggle(toggleName, checked) {
    const toggle = this.element.querySelector(`input[name="${toggleName}"]`)
    if (!toggle) return
    
    // Set toggle state
    toggle.checked = checked ?? true
    
    // Update UI based on toggle state
    const container = toggle.closest('.slider-control-group')
    const slider = container?.querySelector('.dual-range-slider')
    const sliderValueDisplay = container?.querySelector('.slider-value-display')
    const minInput = slider?.querySelector('.slider-min')
    const maxInput = slider?.querySelector('.slider-max')
    const whenEnabled = container?.querySelector('.when-enabled')
    const whenDisabled = container?.querySelector('.when-disabled')
    
    if (checked) {
      // Enable slider inputs
      if (minInput) minInput.disabled = false
      if (maxInput) maxInput.disabled = false
      if (slider) slider.classList.remove('d-none')
      if (sliderValueDisplay) sliderValueDisplay.classList.remove('d-none')
      if (whenEnabled) whenEnabled.classList.remove('d-none')
      if (whenDisabled) whenDisabled.classList.add('d-none')
    } else {
      // Disable slider inputs
      if (minInput) minInput.disabled = true
      if (maxInput) maxInput.disabled = true
      if (slider) slider.classList.add('d-none')
      if (sliderValueDisplay) sliderValueDisplay.classList.add('d-none')
      if (whenEnabled) whenEnabled.classList.add('d-none')
      if (whenDisabled) whenDisabled.classList.remove('d-none')
    }
  }

  /**
   * Clear saved state
   */
  clear() {
    sessionStorage.removeItem(this.storageKey)
  }
}
